import { compareAlphabetically } from "src/utils/sorting";
import type { DocumentsPageItem } from "./Documents.utils";

export type DocumentsPageSortField =
  | "name"
  | "createdBy"
  | "createdAt"
  | "updatedAt"
  | "type";

export type DocumentsPageSortDirection = "asc" | "desc";

const documentsPageSortFields = new Set<DocumentsPageSortField>([
  "name",
  "createdBy",
  "createdAt",
  "updatedAt",
  "type",
]);

export const isDocumentsPageSortField = (
  field: string | undefined
): field is DocumentsPageSortField =>
  field !== undefined &&
  documentsPageSortFields.has(field as DocumentsPageSortField);

type DocumentsPageSortValue = Date | string | null;
type DocumentsPageSortValueGetter = (
  item: DocumentsPageItem
) => DocumentsPageSortValue;

export const sortAlphabetically = <T extends { id: string; name: string }>(
  items: T[]
) =>
  [...items].sort((left, right) =>
    compareAlphabetically(left.name, right.name)
  );

const sortValueByField = {
  name: (item) => item.name.trim(),
  createdBy: (item) => item.createdBy,
  createdAt: (item) => item.createdAt,
  updatedAt: (item) => item.updatedAt,
  type: (item) => item.type,
} satisfies Record<DocumentsPageSortField, DocumentsPageSortValueGetter>;

const compareNonNullSortValues = (
  a: Exclude<DocumentsPageSortValue, null>,
  b: Exclude<DocumentsPageSortValue, null>
) => {
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }

  return compareAlphabetically(String(a), String(b));
};

const applySortDirection = (
  comparison: number,
  direction: DocumentsPageSortDirection
) => (direction === "asc" ? comparison : -comparison);

const compareSortValues = (
  a: DocumentsPageSortValue,
  b: DocumentsPageSortValue,
  direction: DocumentsPageSortDirection
) => {
  if (a === null && b === null) {
    return 0;
  }

  if (a === null) {
    return 1;
  }

  if (b === null) {
    return -1;
  }

  return applySortDirection(compareNonNullSortValues(a, b), direction);
};

export const sortDocumentsPageItems = (
  items: DocumentsPageItem[],
  sortField: DocumentsPageSortField,
  sortDirection: DocumentsPageSortDirection
) => {
  const getSortValue = sortValueByField[sortField];

  return [...items].sort((a, b) => {
    const fieldComparison = compareSortValues(
      getSortValue(a),
      getSortValue(b),
      sortDirection
    );

    return fieldComparison || compareAlphabetically(a.id, b.id);
  });
};
